import { FC, useCallback, useRef } from 'react';
import { useRouter } from 'shared-routing';
import { DiningListTemplate } from 'voyager';
import type { JourneyPickerSearchParams } from 'voyager';

import SectionContainer from '@common/components/SectionContainer';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useGetTablesPage } from '@features/tables/hooks/useGetTablesPage';
import { useTranslations } from '@lang';
import { parseDateToString } from '@utils/helpers';

import type { DiningV2Props } from './DiningV2.types';
import { useDiningRestaurants, usePropertyContext } from './queries';
import { bookingPath, restaurantBookingSegment } from '@features/tables/bookingRoute';
import { useDiningList } from './useDiningList';

export const DiningV2: FC<DiningV2Props> = ({ changeJourneyParam }) => {
  const { t } = useTranslations();
  const router = useRouter();
  const { items, isLoading } = useDiningList();
  const { data: restaurantsData } = useDiningRestaurants();

  const { propertyId, language } = usePropertyContext();
  const { data: tablesPage } = useGetTablesPage(propertyId, language);
  const tablesPageTitle = (tablesPage as { title?: string } | null | undefined)?.title;

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  const searchRef = useRef<HTMLDivElement>(null);
  const handleEditSearch = useCallback(() => {
    searchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const goToReservation = (restaurantId: string) => {
    const restaurants = Array.isArray(restaurantsData) ? restaurantsData : [];
    const restaurant = restaurants.find((entry) => entry.providerId === restaurantId);
    router.push({
      pathname: bookingPath(restaurant ? restaurantBookingSegment(restaurant) : restaurantId),
    });
  };

  const handleSearchComplete = useCallback(
    (params: JourneyPickerSearchParams) => {
      changeJourneyParam('fromDate', parseDateToString(params.fromDate));
    },
    [changeJourneyParam]
  );

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper ref={searchRef}>
        <JourneyPickerInnerWrapper>
          <JourneyPickerV2
            dateMode="single"
            dateContext="dining"
            showGuests
            categories={navCategories}
            category="tables"
            onCategoryChange={handleNavCategoryChange}
            onSearchComplete={handleSearchComplete}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <DiningListTemplate
          items={items}
          isLoading={isLoading}
          onSelectItem={goToReservation}
          onEditSearch={handleEditSearch}
          labels={{
            title: tablesPageTitle || t('app.dining'),
            quickView: t('dining.quickView'),
            reserve: t('dining.reserve'),
            viewMenu: t('dining.viewMenu'),
            unavailable: t('common.unavailable'),
            editSearch: t('common.editSearch'),
            noResults: t('dining.noResults'),
            noImage: t('basket.noImageLabel'),
            close: t('dining.close'),
            previousImage: t('product.previousImage'),
            nextImage: t('product.nextImage'),
            openingHours: t('dining.openingHours'),
            menus: t('dining.menus'),
            showMore: t('common.showMore'),
            showLess: t('common.showLess'),
            today: t('common.today'),
          }}
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
